import {useEffect, useRef, useState} from "react";
import {identify, setProfile, setRegister, setTrack, trackPurchase} from "./tracking";
import {dataFetch, setPrice} from "./lib";
import {useTranslation} from "react-i18next";
import {Link, useNavigate, useParams, useSearchParams} from "react-router-dom";
import {getStripe} from "./stripe";

export const usePayment = ({data, custom: customDefault = {}, force = false}) => {
  const
    {t} = useTranslation('common'),
    navigate = useNavigate(),
    {uid} = useParams(),
    [searchParams] = useSearchParams()

  const
    [custom, setCustom] = useState(customDefault),
    [message, setMessage] = useState(null),
    mountedRef = useRef(true),
    [isPayment, setIsPayment] = useState(false),
    dataRef = useRef(data),
    customRef = useRef(custom)

  // async use?
  if (uid && !window.localStorage.getItem('uid')) {
    window._uid = uid
    window.localStorage.setItem('uid', uid)
  }

  if (searchParams.get('email') && !window.localStorage.getItem('email')) {
    window._email = searchParams.get('email')
    window.localStorage.setItem('email', window._email)
  }

  if (searchParams.get('params') && !dataRef.current && !force) {
    try {
      dataRef.current = JSON.parse(searchParams.get('params'))
      setPrice(dataRef.current)
    } catch (ex) {
      console.error(ex)
      // setTrack('checkout_error', {message: 'invalid params'})
      // catchError(ex, 'failed parse params on success page', false, false)
    }
  }

  useEffect(() => {
    if (uid && !window.localStorage.getItem('identify'))
      identify(uid, window._email)

    const clientSecret = searchParams.get('payment_intent_client_secret')

    if (clientSecret) {
      const setResults = async () => {
        let line = 0

        try {
          const stripe = await getStripe()
          line = 1
          const {paymentIntent} = await stripe.retrievePaymentIntent(clientSecret)
            // paymentIntent = {status: 'canceled'}

          setCustom(prev => ({...prev, payment_method_id: paymentIntent.payment_method}))
          window.sessionStorage.setItem('payment_method_id', paymentIntent.payment_method || '')

          line = 2

          if (process.env.REACT_APP_DEBUG)
            console.log('paymentIntent', paymentIntent)

          // https://docs.stripe.com/api/events/types?lang=php#event_types-payment_intent.payment_failed
          // succeeded - ✅ Платёж прошёл
          // processing - ⏳ Stripe ещё обрабатывает (может завершиться позже)
          // requires_payment_method - ❌ Платёж не прошёл, нужна другая карта
          // requires_action - ⚠️ Требуется 3DS или другое подтверждение
          // canceled - ❌ Платёж отменён
          // requires_confirmation - ⚠️ Не был подтверждён (confirm() не вызван)

        line = 3

          let message

          switch (paymentIntent.status) {
            case 'succeeded':
              setRegister({order_id: clientSecret})
              setProfile({
                payment_method: window.sessionStorage.getItem('payment_method') || 'Wallet',
                stripe: clientSecret,
                plan_purchase: true
              })
              trackPurchase(dataRef.current)
              break

            case 'processing':
              // ⏳ ожидается подтверждение
              break
            case 'requires_confirmation':
              // message = "⚠️ Your payment is awaiting confirmation by our system. Please wait a moment — we’ll update your subscription automatically."
              break

            case 'requires_payment_method':
              message = "❌ Payment failed. Please try again with a different card or method."
              break
            case 'requires_action':
              message = "⚠️ Additional authentication is required to complete your payment."
              break
            case 'canceled':
              message = "❌ Payment was canceled. Please try again if this was a mistake."
              break
            default:
              message = `❌ Something went wrong with your payment (#${paymentIntent.status}). Please try again.`
          }
line = 4
          if (message) {
            setCustom(prev => ({...prev, paymentId: clientSecret}))
            catchError({message, msg: `payment intent status: ${paymentIntent.status}`}, 'status usePayment', false)
line = 5
            setTimeout(() => {
              window.localStorage.removeItem('success')
              setMessage(
                <>
                  {message.split('try again')[0]}
                  <Link to={-1}>try again</Link>
                  {message.split('try again')[1]}
                </>
              )
            }, 100)
          }

          setIsPayment(true)
        } catch (err) {
          catchError(err, `catch usePayment line=${line}`, false, false)
        }
      }
      setResults().then(() => {})

      const type = searchParams.get('type')

      navigate(window.location.pathname + (type ? `/?type=${type}` : ''), {replace: true})
    }

    return () => {
      mountedRef.current = false
    }
  }, [])

  async function serializeError(err) {
    const isErrorInstance =
      err instanceof Error ||
      Object.prototype.toString.call(err) === '[object Error]'

    if (isErrorInstance && err !== null) {
      const result = {
        kind: err.type || 'network',
        name: err.name,
        message: err.message,
        stack: err.stack,
      }

      // Добавляем любые дополнительные поля, которые не в прототипе
      for (const key in err) {
        if (
          Object.prototype.hasOwnProperty.call(err, key) &&
          !(key in result)
        ) {
          result[key] = err[key]
        }
      }

      // Рекурсивная сериализация cause (если есть)
      if (err.cause && typeof err.cause === 'object') {
        result.cause = serializeError(err.cause)
      }

      return result
    }

    // 👇 Кейс: ошибка от fetch
    if (typeof err === 'object' && err?.ok === false) {
      let body
      try {
        body = await err?.json()
      } catch {
        try {
          body = await err?.text()
        } catch {
          body = null
        }
      }

      return {
        kind: 'fetch',
        status: err?.status,
        statusText: err?.statusText,
        url: err?.url,
        body,
      }
    }

    return err

    // Если это обычный объект — сохранить все поля
    // if (typeof err === 'object' && err !== null) {
    //   return {...err}
    // }

    // // Если это строка, число, null, undefined и т.п.
    // return {
    //   value: String(err),
    //   type: typeof err,
    // }
  }

  const catchError = async (err, error_desc, toFallback = true, toMessage = true) => {
    const msg =
      typeof err === 'string'
        ? err
        : (
          typeof err.message === 'string'
            ? err.message
            : t('error')
        )

    let error

    try {
      error = await serializeError(err)

      // if (desc) {
      //   if (typeof error === 'string')
      //     error = {
      //       message: error,
      //       desc
      //     }
      //   else
      //     error['desc'] = desc
      // }

      if (error.message === msg)
        delete error.message
    } catch (ex) {
      error = {
        msg: ex.message,
        desc: 'Cannot serialize error'
      }
    }

    if (toMessage && mountedRef.current)
      setMessage(msg)

    setTrack('checkout_error', {message: msg, ...customRef.current, error, error_desc})

    if (!toFallback)
      return

    dataFetch(
      process.env.REACT_APP_API_URI + 'web-payments/fallback',
      {
        ...dataRef.current,
        ...customRef.current,
        error,
        error_desc,
      },
      // null,
      // 'POST',
      // null,
      // once,
    )
  }

  return {
    custom, setCustom,
    message, setMessage,
    isPayment,
    catchError,

    getReturnUrl: (nextUrl, walletType = false) => {
      let return_url = nextUrl.startsWith('http')
        ? nextUrl
        : window.location.origin + nextUrl

      if (!nextUrl.startsWith('http')) {
        const params =
          dataRef.current
            ?
            encodeURIComponent(JSON.stringify(
              setPrice(dataRef.current, true)
            ))
            : ''

        let p = null

        if (walletType) {
          p = return_url.split('/?')
          return_url = p[0]
        }

        return_url += `/${window._uid || window.localStorage.getItem('uid')}/?email=${encodeURIComponent(window._email || window.localStorage.getItem('email'))}&session_uid=${window.session_uid}`

        if (walletType && p && p.length > 1)
          return_url += '&' + p[1]

        if (params)
          return_url += `&params=${params}`
      }

      return return_url
    }
  }
}